/**
 * The investor's own activity, assembled from the rows they already own.
 *
 * A leaf module: no runtime imports, so the ordering and naming rules are
 * testable without a client or a render.
 *
 * WHY DERIVED, NOT READ FROM AN EVENT TABLE
 * -----------------------------------------
 * `analytics_events` already records a per-user stream, but its only SELECT
 * policy is admin-only (`super_admin`/`analyst`) — an investor cannot read
 * their own rows. Backing this on it would mean opening a new read path onto
 * telemetry, and its event names (`startup_detail_viewed`,
 * `dealflow_status_changed`) are instrumentation language rather than anything
 * to show a person.
 *
 * THE HONEST LIMIT
 * ----------------
 * These are FACTS WITH TIMESTAMPS, not a transition history. A deal that went
 * screening → interested → rejected contributes its request and the founder's
 * answer, not every stage it passed through: nothing in the schema records
 * those. Reconstructing them from `updated_at` would be inventing a sequence
 * nobody observed. A true history needs an append-only events table, and that
 * is a bigger change than this.
 */

export type InvestorActivityKind =
  | 'deck_received'
  | 'access_requested'
  | 'access_granted'
  | 'access_declined'
  /** The investor stood down — written when they pass on the deal. */
  | 'access_withdrawn'
  | 'dd_concluded';

export interface InvestorActivityEvent {
  /** Stable and unique; these are rendered with `.map()`. */
  id: string;
  kind: InvestorActivityKind;
  /** ISO timestamp. Always present — undated rows are dropped, not guessed at. */
  at: string;
  /** Display name of the company the event is about. */
  company: string;
  /** `startup_profiles.id` where one is known, for navigation. */
  startupId?: string;
}

/** The subset of each source row this module reads. */
export interface ActivityInputs {
  submissions: { id: string; company_name: string | null; created_at: string | null }[];
  requests: {
    startup_id: string;
    status: string | null;
    created_at: string | null;
    responded_at: string | null;
  }[];
  /** Pipeline deals, used only to put a NAME on a startup id. */
  deals: { startupId: string; company: string }[];
  reports: { id: string; startup_id: string; concluded_at: string | null }[];
}

/**
 * Shown when a request or report points at a company that has since left the
 * pipeline. The event is still real and still theirs — dropping it would
 * silently shorten someone's own history — so it is kept, unnamed.
 */
const UNKNOWN_COMPANY = 'Unknown company';

/** Which answer the founder (or the investor) gave. */
function answerKind(status: string | null): InvestorActivityKind | null {
  if (status === 'approved') return 'access_granted';
  if (status === 'rejected') return 'access_declined';
  // Not a founder decision at all: the investor passed and the request was
  // stood down. Reporting it as "declined" would blame the wrong party.
  if (status === 'withdrawn') return 'access_withdrawn';
  return null;
}

export function buildInvestorActivity(inputs: ActivityInputs): InvestorActivityEvent[] {
  const nameFor = new Map(inputs.deals.map((d) => [d.startupId, d.company]));
  const events: InvestorActivityEvent[] = [];

  for (const s of inputs.submissions) {
    if (!s.created_at) continue;
    events.push({
      id: `deck_received:${s.id}`,
      kind: 'deck_received',
      at: s.created_at,
      company: s.company_name || UNKNOWN_COMPANY,
    });
  }

  for (const r of inputs.requests) {
    const company = nameFor.get(r.startup_id) ?? UNKNOWN_COMPANY;

    if (r.created_at) {
      events.push({
        id: `access_requested:${r.startup_id}`,
        kind: 'access_requested',
        at: r.created_at,
        company,
        startupId: r.startup_id,
      });
    }

    // The answer is a SECOND event at its own time. Collapsing it into the
    // request would lose the wait between them, which is the interesting part.
    const kind = answerKind(r.status);
    if (kind && r.responded_at) {
      events.push({
        id: `${kind}:${r.startup_id}`,
        kind,
        at: r.responded_at,
        company,
        startupId: r.startup_id,
      });
    }
  }

  for (const rep of inputs.reports) {
    if (!rep.concluded_at) continue;
    events.push({
      id: `dd_concluded:${rep.id}`,
      kind: 'dd_concluded',
      at: rep.concluded_at,
      company: nameFor.get(rep.startup_id) ?? UNKNOWN_COMPANY,
      startupId: rep.startup_id,
    });
  }

  // Newest first. Undated rows never reach here: an event that cannot be placed
  // in time would be making a claim about when it happened wherever it landed.
  return events.sort((a, b) => b.at.localeCompare(a.at));
}

/** One-line description for a row. Kept here so the copy has one home. */
export function describeActivity(event: InvestorActivityEvent): string {
  switch (event.kind) {
    case 'deck_received':
      return `${event.company} submitted a deck`;
    case 'access_requested':
      return `You requested data room access from ${event.company}`;
    case 'access_granted':
      return `${event.company} opened their data room`;
    case 'access_declined':
      return `${event.company} declined your data room request`;
    case 'access_withdrawn':
      return `You withdrew your data room request to ${event.company}`;
    case 'dd_concluded':
      return `You concluded diligence on ${event.company}`;
  }
}
